import { createAsyncThunk } from '@reduxjs/toolkit';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import {
  getImpersonateId,
  getImpersonateKitId,
  impersonate,
  impersonateKit,
  signOut,
} from 'store/user';
import {
  deleteImpersonationCookie,
  deleteImpersonationKitCookie,
  setImpersonationCookie,
  setImpersonationKitCookie,
} from 'utils/impersonation';

export const impersonateUser = createAsyncThunk(
  'user/impersonateUser',
  async (impersonateId: string, { dispatch }) => {
    dispatch(impersonate(impersonateId));
  },
);

const useImpersonate = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const impersonateId = useSelector(getImpersonateId);
  const impersonateKitId = useSelector(getImpersonateKitId);

  const impersonateFn = useCallback(
    async (impersonateMainAccountId: string, kitId?: string | null) => {
      await dispatch(impersonateUser(impersonateMainAccountId)).unwrap();

      // Set mainaccount cookie for persistence across page refresh
      setImpersonationCookie(impersonateMainAccountId, { scope: 'host-only' });

      // Set kit impersonation if kit ID provided
      if (kitId) {
        dispatch(impersonateKit(kitId));
        setImpersonationKitCookie(kitId, {});
        navigate(`/results/${kitId}`);
      } else {
        dispatch(impersonateKit(undefined));
        deleteImpersonationKitCookie();
        navigate('/results');
      }
    },
    [dispatch, navigate],
  );

  const stopImpersonating = useCallback(async () => {
    // Clear cached data for the impersonated user
    await queryClient.removeQueries();
    // Clear impersonation state
    await dispatch(impersonateUser('')).unwrap();
    dispatch(impersonateKit(undefined));
    // Clear both impersonation cookies
    deleteImpersonationCookie({ scope: 'host-only' });
    deleteImpersonationKitCookie();
    // Clear local auth state without calling Auth.signOut()
    // This preserves the practitioner portal session since they share the same JWT
    dispatch(signOut());
    window.close();
  }, [dispatch, queryClient]);

  return {
    impersonate: impersonateFn,
    impersonateId,
    impersonateKitId,
    stopImpersonating,
  };
};
export default useImpersonate;
